import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { MapPin, Search, PawPrint, ShieldCheck, ArrowRight, X, Mail, ChevronRight, LayoutDashboard, Ticket, CheckCircle2, Clock3, Store, Activity, Dog, Cat, LogOut, Plus, Trash2, KeyRound, Upload, TrendingDown } from "lucide-react";
import InsurancePage from "./components/InsurancePage";
import { Calendar } from "@/components/ui/calendar";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const demoEmail = "demo@tailwhisk.in";

const DOG_BREEDS = ["Indie / Mixed", "Labrador Retriever", "Golden Retriever", "Pomeranian", "German Shepherd", "Beagle", "Shih Tzu", "Pug", "Rottweiler", "Dachshund", "Boxer", "Siberian Husky"];
const CAT_BREEDS = ["Indie / Mixed", "Persian", "Siamese", "Maine Coon", "Bengal", "British Shorthair", "Ragdoll", "Russian Blue"];
const GROOMING_SUGGESTIONS = [
  { name: "Bath & blow dry", duration: "60 min", price: 799 },
  { name: "Full grooming", duration: "90 min", price: 1299 },
  { name: "Haircut & styling", duration: "75 min", price: 999 },
  { name: "Nail trimming", duration: "15 min", price: 199 },
  { name: "Ear cleaning", duration: "15 min", price: 149 },
  { name: "Teeth brushing", duration: "15 min", price: 249 },
  { name: "De-shedding treatment", duration: "60 min", price: 899 },
  { name: "Flea & tick bath", duration: "60 min", price: 999 },
  { name: "Paw pad trim", duration: "20 min", price: 199 },
  { name: "Anal gland expression", duration: "15 min", price: 299 },
  { name: "Puppy's first groom", duration: "45 min", price: 699 },
  { name: "Spa & aromatherapy", duration: "75 min", price: 1199 },
];
const CLINIC_SUGGESTIONS = [
  { name: "Wellness check-up", duration: "30 min", price: 600 },
  { name: "Vaccination visit", duration: "30 min", price: 450 },
  { name: "Deworming", duration: "15 min", price: 350 },
  { name: "Dental check", duration: "30 min", price: 550 },
  { name: "Diet consultation", duration: "20 min", price: 400 },
];

const trackEvent = (event, email, metadata = {}) => {
  axios.post(`${API}/analytics`, { event, email: email || null, metadata }).catch(() => {});
};
const partnerAuthHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });
const adminAuthHeader = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

const resolveImage = (src) => {
  if (!src) return null;
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  if (src.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${src}`;
  return src;
};

const isAdminRoute = () => typeof window !== "undefined" && window.location.pathname.startsWith("/admin-console");
const publicSlug = () => {
  if (typeof window === "undefined") return null;
  const m = window.location.pathname.match(/^\/p\/([^/]+)/);
  return m ? m[1] : null;
};

function App() {
  const [mode, setMode] = useState(() => (isAdminRoute() ? "admin" : "marketplace"));
  const [email, setEmail] = useState("");
  const [user, setUser] = useState(null);
  const [auth, setAuth] = useState(false);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [providers, setProviders] = useState([]);
  const [query, setQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("overview");
  const [coupons, setCoupons] = useState([]);
  const [chosenDate, setChosenDate] = useState(null);
  const [chosenSlot, setChosenSlot] = useState(null);
  const [chosenService, setChosenService] = useState(null);
  const [chosenOffer, setChosenOffer] = useState(null);
  const [notice, setNotice] = useState("");
  const [pendingRequest, setPendingRequest] = useState(null);
  const [custName, setCustName] = useState("");
  const [custPhone, setCustPhone] = useState("");
  const [petName, setPetName] = useState("");
  const [petNotes, setPetNotes] = useState("");
  const [species, setSpecies] = useState("");
  const [breed, setBreed] = useState("");
  const [breedOther, setBreedOther] = useState("");
  // Partner
  const [partner, setPartner] = useState(() => {
    const raw = localStorage.getItem("pawcare_partner");
    return raw ? JSON.parse(raw) : null;
  });
  const [partnerUser, setPartnerUser] = useState("");
  const [partnerPw, setPartnerPw] = useState("");
  const [partnerCoupons, setPartnerCoupons] = useState([]);
  const [partnerProvider, setPartnerProvider] = useState(null);
  const [newOfferLabel, setNewOfferLabel] = useState("");
  const [newOfferDetail, setNewOfferDetail] = useState("");
  const [newOfferServiceIds, setNewOfferServiceIds] = useState([]);
  const [newServiceName, setNewServiceName] = useState("");
  const [newServiceDuration, setNewServiceDuration] = useState("60 min");
  const [newServicePrice, setNewServicePrice] = useState("");
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [queueStatusTab, setQueueStatusTab] = useState("pending");
  const [queueDateFilter, setQueueDateFilter] = useState("all");
  const [rejectionStats, setRejectionStats] = useState(null);
  const photoInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  // Admin
  const [admin, setAdmin] = useState(() => {
    const raw = localStorage.getItem("pawcare_admin");
    return raw ? JSON.parse(raw) : null;
  });
  const [adminLoginUser, setAdminLoginUser] = useState("");
  const [adminLoginPw, setAdminLoginPw] = useState("");
  const [onboardForm, setOnboardForm] = useState({ username: "", password: "", name: "", business_type: "Grooming", area: "", pincode: "", phone: "", description: "", address: "", maps_url: "" });
  const [profileForm, setProfileForm] = useState({ name: "", business_type: "Grooming", area: "", pincode: "", phone: "", description: "" });
  const [slotSettings, setSlotSettings] = useState({ start_time: "09:00", end_time: "18:00", break_minutes: 0, closed_days: [] });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [partnerFunnel, setPartnerFunnel] = useState(null);
  const [adminProviders, setAdminProviders] = useState([]);
  const [editingProvider, setEditingProvider] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [zones, setZones] = useState([]);
  const [newZoneName, setNewZoneName] = useState("");
  const [newZoneAreas, setNewZoneAreas] = useState("");
  const [newZonePincodes, setNewZonePincodes] = useState("");
  const [editingZone, setEditingZone] = useState(null);
  // Admin dashboard data
  const [adminSummary, setAdminSummary] = useState(null);
  const [adminEvents, setAdminEvents] = useState([]);
  const [adminFunnel, setAdminFunnel] = useState([]);
  const [adminPartners, setAdminPartners] = useState([]);
  const [adminForm, setAdminForm] = useState({ username: "", password: "", provider_id: "" });
  const [editCreds, setEditCreds] = useState(null); // {current_username, provider_name, new_username, new_password}

  const openEditCreds = (account) => setEditCreds({ current_username: account.username, provider_name: account.provider_name, new_username: account.username, new_password: "" });
  const closeEditCreds = () => setEditCreds(null);
  const saveEditCreds = async () => {
    if (!editCreds) return;
    const payload = {};
    if (editCreds.new_username && editCreds.new_username !== editCreds.current_username) payload.new_username = editCreds.new_username.trim();
    if (editCreds.new_password) payload.new_password = editCreds.new_password.trim();
    if (!Object.keys(payload).length) return show("Change the username or set a new password first");
    try {
      await axios.patch(`${API}/admin/partners/${encodeURIComponent(editCreds.current_username)}`, payload, adminAuthHeader(admin.token));
      await loadAdmin();
      closeEditCreds();
      show("Credentials updated");
    } catch (e) { show(e.response?.data?.detail || "Could not update credentials"); }
  };

  const show = (text) => { setNotice(text); setTimeout(() => setNotice(""), 3800); };

  const loadProviders = async (searchQ, tFilter) => {
    const p = await axios.get(`${API}/providers`, { params: { q: searchQ ?? query, type: tFilter ?? typeFilter } });
    setProviders(p.data);
  };
  const loadUserCoupons = async (overrideEmail) => {
    const target = overrideEmail || user?.email;
    if (!target) return;
    const r = await axios.get(`${API}/coupons`, { params: { email: target } });
    setCoupons(r.data);
    try {
      const p = await axios.get(`${API}/profile`, { params: { email: target } });
      if (p.data?.name) setCustName((prev) => prev || p.data.name);
      if (p.data?.phone) setCustPhone((prev) => prev || p.data.phone);
      if (p.data?.last_pet_name) setPetName((prev) => prev || p.data.last_pet_name);
      if (p.data?.last_species) setSpecies((prev) => prev || p.data.last_species.toLowerCase());
      if (p.data?.last_breed) setBreed((prev) => prev || p.data.last_breed);
    } catch (e) {}
  };

  useEffect(() => { loadProviders(""); axios.get(`${API}/zones`).then((r) => setZones(r.data)).catch(() => {}); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const slug = publicSlug();
    if (slug) {
      axios.get(`${API}/providers/${slug}`).then((r) => {
        setSelected(r.data); setTab("coupon");
        setChosenService(r.data.services[0] || null);
        trackEvent("provider_viewed", null, { provider_id: slug, source: "public_link" });
      }).catch(() => {});
    }
  }, []);
  useEffect(() => { if (user) loadUserCoupons(); /* eslint-disable-next-line */ }, [user]);

  // Partner data
  const loadPartner = async () => {
    if (!partner?.token) return;
    try {
      const [me, cp, rs, an] = await Promise.all([
        axios.get(`${API}/partner/me`, partnerAuthHeader(partner.token)),
        axios.get(`${API}/partner/coupons`, partnerAuthHeader(partner.token)),
        axios.get(`${API}/partner/rejection-stats`, partnerAuthHeader(partner.token)),
        axios.get(`${API}/partner/analytics`, partnerAuthHeader(partner.token)),
      ]);
      setPartnerProvider(me.data.provider);
      setPartnerCoupons(cp.data);
      setRejectionStats(rs.data);
      setPartnerFunnel(an.data);
      const prov = me.data.provider || {};
      setProfileForm({
        name: prov.name || "",
        business_type: (prov.type || "").toLowerCase().includes("vet") ? "Clinic" : "Grooming",
        area: prov.area || "", pincode: prov.pincode || "",
        phone: prov.phone || "", description: prov.description || "",
      });
      const oh = prov.operating_hours || {};
      setSlotSettings({
        start_time: oh.start_time || "09:00",
        end_time: oh.end_time || "18:00",
        break_minutes: oh.break_minutes ?? 0,
        closed_days: oh.closed_days || [],
      });
    } catch (e) {
      if (e.response?.status === 401) partnerLogout();
    }
  };
  useEffect(() => { if (mode === "partner" && partner?.token) loadPartner(); /* eslint-disable-next-line */ }, [mode, partner]);

  const uploadPhoto = async (fileObj) => {
    if (!fileObj) return;
    const form = new FormData();
    form.append("file", fileObj);
    try {
      setUploadingPhoto(true);
      await axios.post(`${API}/partner/photo`, form, { headers: { Authorization: `Bearer ${partner.token}`, "Content-Type": "multipart/form-data" } });
      await loadPartner(); await loadProviders("");
      show("Photo updated — customers will see it instantly");
    } catch (e) { show(e.response?.data?.detail || "Upload failed"); }
    finally { setUploadingPhoto(false); }
  };

  // Admin data
  const loadAdmin = async () => {
    if (!admin?.token) return;
    try {
      const [s, e, f, pt, pv] = await Promise.all([
        axios.get(`${API}/admin/summary`, adminAuthHeader(admin.token)),
        axios.get(`${API}/admin/events`, adminAuthHeader(admin.token)),
        axios.get(`${API}/admin/funnel`, adminAuthHeader(admin.token)),
        axios.get(`${API}/admin/partners`, adminAuthHeader(admin.token)),
        axios.get(`${API}/admin/providers`, adminAuthHeader(admin.token)),
      ]);
      setAdminSummary(s.data); setAdminEvents(e.data); setAdminFunnel(f.data); setAdminPartners(pt.data); setAdminProviders(pv.data);
    } catch (err) {
      if (err.response?.status === 401) adminLogout();
    }
  };
  useEffect(() => { if (mode === "admin" && admin?.token) loadAdmin(); /* eslint-disable-next-line */ }, [mode, admin]);

  const adminLoginSubmit = async () => {
    try {
      const r = await axios.post(`${API}/admin/login`, { username: adminLoginUser, password: adminLoginPw });
      localStorage.setItem("pawcare_admin", JSON.stringify(r.data));
      setAdmin(r.data); setAdminLoginUser(""); setAdminLoginPw("");
      show("Welcome, admin");
    } catch (e) { show(e.response?.data?.detail || "Invalid credentials"); }
  };
  const adminLogout = () => {
    if (admin?.token) axios.post(`${API}/admin/logout`, {}, adminAuthHeader(admin.token)).catch(() => {});
    localStorage.removeItem("pawcare_admin");
    setAdmin(null); setAdminSummary(null); setAdminEvents([]); setAdminFunnel([]); setAdminPartners([]);
  };

  const runSearch = () => { trackEvent("location_selected", user?.email, { query }); loadProviders(query, typeFilter); };
  const quickPin = (pin) => { setQuery(pin); trackEvent("location_selected", user?.email, { query: pin, source: "quick_pin" }); loadProviders(pin, typeFilter); };
  const applyTypeFilter = (t) => { setTypeFilter(t); loadProviders(query, t === "ClinicComingSoon" || t === "EventsComingSoon" ? "__coming_soon__" : t); };

  const sendOtp = async () => {
    if (!email.includes("@")) return show("Enter a valid email address");
    try {
      const r = await axios.post(`${API}/auth/send-otp`, { email });
      setSent(true); show(r.data.message || "Code sent — check your email");
    } catch (e) { show(e.response?.data?.detail || "Could not send the code. Try again."); }
  };
  const verify = async () => {
    try {
      const r = await axios.post(`${API}/auth/verify-otp`, { email, code, name: custName || "Bengaluru pet parent" });
      setUser(r.data.user); setAuth(false); setSent(false); setCode("");
      show("Welcome to TailWhisk");
      if (pendingRequest) {
        await submitCoupon(r.data.user.email, pendingRequest);
        setPendingRequest(null);
      }
    } catch (e) { show(e.response?.data?.detail || "That code didn't work. Try again."); }
  };

  const openProvider = async (p) => {
    const r = await axios.get(`${API}/providers/${p.id}`);
    setSelected(r.data); setTab("overview");
    setChosenDate(null); setChosenSlot(null); setChosenOffer(null);
    setChosenService(r.data.services[0]);
    setAvailableSlots([]);
    trackEvent("provider_viewed", user?.email, { provider_id: p.id });
  };

  const toLocalISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  useEffect(() => {
    if (!selected || !chosenService) { setAvailableSlots([]); return; }
    axios.get(`${API}/providers/${selected.id}/available-slots`, { params: { service_id: chosenService.id } })
      .then((r) => { setAvailableSlots(r.data); setChosenDate(null); setChosenSlot(null); })
      .catch(() => setAvailableSlots([]));
  }, [selected?.id, chosenService?.id]);

  const slotDates = useMemo(() => Array.from(new Set(availableSlots.filter((s) => s.available).map((s) => s.date))).sort(), [availableSlots]);
  const timesForDate = useMemo(() => {
    if (!chosenDate) return [];
    const iso = toLocalISO(chosenDate);
    return availableSlots.filter((s) => s.date === iso);
  }, [availableSlots, chosenDate]);

  const pickSlot = (slot) => { setChosenSlot(slot); trackEvent("slot_selected", user?.email, { slot_id: slot.id, provider_id: selected.id }); };
  const pickOffer = (offerId) => {
    const off = selected.offers.find((o) => o.id === offerId) || null;
    setChosenOffer(off);
    if (off) trackEvent("offer_selected", user?.email, { offer_id: off.id, provider_id: selected.id });
  };

  const submitCoupon = async (emailOverride, payloadOverride) => {
    const payload = payloadOverride || {
      provider_id: selected.id, service_id: chosenService.id,
      slot_id: chosenSlot.id, offer_id: chosenOffer?.id || "",
      email: emailOverride || user?.email,
      customer_name: custName, phone: custPhone,
      pet_name: petName, species, breed, breed_other: breedOther, pet_notes: petNotes,
    };
    try {
      const r = await axios.post(`${API}/coupons`, { ...payload, email: emailOverride || user?.email });
      setSelected(null); setChosenSlot(null); setChosenOffer(null); setChosenDate(null);
      await loadUserCoupons(emailOverride || user?.email);
      setMode("coupons");
      show(`Coupon ${r.data.coupon.code} is pending — partner will confirm`);
    } catch (e) {
      show(e.response?.data?.detail || "Could not create coupon");
    }
  };

  const requestCoupon = () => {
    const needsOffer = (selected?.offers || []).length > 0;
    if (!chosenService || !chosenSlot || (needsOffer && !chosenOffer)) return show(needsOffer ? "Pick a service, date, slot, and offer" : "Pick a service, date, and slot");
    if (!custName.trim()) return show("Enter your name");
    if ((custPhone.match(/\d/g) || []).length < 10) return show("Enter a valid 10-digit phone number");
    if (!petName.trim()) return show("Enter your pet's name");
    if (!species) return show("Choose dog or cat");
    if (!breed) return show("Pick a breed");
    if (breed === "Other" && !breedOther.trim()) return show("Type the breed name");
    const payload = { provider_id: selected.id, service_id: chosenService.id, slot_id: chosenSlot.id, offer_id: chosenOffer?.id || "", customer_name: custName, phone: custPhone, pet_name: petName, species, breed, breed_other: breedOther, pet_notes: petNotes };
    if (!user) { setPendingRequest(payload); setAuth(true); return; }
    submitCoupon(user.email);
  };

  // ---- Partner ----
  const partnerLogin = async () => {
    try {
      const r = await axios.post(`${API}/partner/login`, { username: partnerUser, password: partnerPw });
      const session = r.data;
      localStorage.setItem("pawcare_partner", JSON.stringify(session));
      setPartner(session); setPartnerUser(""); setPartnerPw("");
      show(`Welcome ${session.provider_name}`);
    } catch (e) { show(e.response?.data?.detail || "Invalid credentials"); }
  };
  const partnerLogout = () => { localStorage.removeItem("pawcare_partner"); setPartner(null); setPartnerCoupons([]); setPartnerProvider(null); };
  const confirmPartnerCoupon = async (couponId) => {
    try {
      const r = await axios.post(`${API}/partner/coupons/${couponId}/confirm`, {}, partnerAuthHeader(partner.token));
      await loadPartner();
      show(r.data.email?.sent ? "Confirmed · confirmation email sent to the customer" : "Confirmed · but the confirmation email failed to send");
    } catch (e) { show(e.response?.data?.detail || "Could not confirm"); }
  };
  const openReject = (coupon) => { setRejectTarget(coupon); setRejectReason(""); };
  const closeReject = () => { setRejectTarget(null); setRejectReason(""); };
  const submitReject = async () => {
    if (!rejectReason.trim()) return show("Share a reason the customer can understand");
    try {
      const r = await axios.post(`${API}/partner/coupons/${rejectTarget.id}/reject`, { reason: rejectReason }, partnerAuthHeader(partner.token));
      closeReject();
      await loadPartner();
      show("Rejected · customer notifications are currently paused");
    } catch (e) { show(e.response?.data?.detail || "Could not reject"); }
  };
  const createOffer = async () => {
    if (!newOfferLabel.trim()) return show("Offer label is required");
    try {
      await axios.post(`${API}/partner/offers`, { label: newOfferLabel, detail: newOfferDetail, service_ids: newOfferServiceIds }, partnerAuthHeader(partner.token));
      setNewOfferLabel(""); setNewOfferDetail(""); setNewOfferServiceIds([]);
      await loadPartner(); await loadProviders("");
      show("Offer published — customers will see it instantly");
    } catch (e) { show(e.response?.data?.detail || "Could not add offer"); }
  };
  const deleteOffer = async (offerId) => {
    try {
      await axios.delete(`${API}/partner/offers/${offerId}`, partnerAuthHeader(partner.token));
      await loadPartner(); await loadProviders("");
      show("Offer removed");
    } catch (e) { show(e.response?.data?.detail || "Could not remove"); }
  };

  const createService = async (preset) => {
    const name = preset?.name || newServiceName;
    const duration = preset?.duration || newServiceDuration;
    const priceRaw = preset?.price ?? newServicePrice;
    if (!name.trim()) return show("Service name is required");
    const price = parseInt(priceRaw, 10) || 0;
    try {
      await axios.post(`${API}/partner/services`, { name, duration, price }, partnerAuthHeader(partner.token));
      if (!preset) { setNewServiceName(""); setNewServiceDuration("60 min"); setNewServicePrice(""); }
      await loadPartner(); await loadProviders("");
      show(`Added "${name}"`);
    } catch (e) { show(e.response?.data?.detail || "Could not add service"); }
  };
  const deleteService = async (serviceId) => {
    try {
      await axios.delete(`${API}/partner/services/${serviceId}`, partnerAuthHeader(partner.token));
      await loadPartner(); await loadProviders("");
      show("Service removed");
    } catch (e) { show(e.response?.data?.detail || "Could not remove"); }
  };

  // ---- Admin ----
  const createPartnerAccount = async () => {
    try {
      await axios.post(`${API}/admin/partners`, adminForm, adminAuthHeader(admin.token));
      setAdminForm({ username: "", password: "", provider_id: "" });
      await loadAdmin();
      show("Partner account created");
    } catch (e) { show(e.response?.data?.detail || "Could not create partner"); }
  };

  const onboardNewPartner = async () => {
    if (!onboardForm.name.trim()) return show("Business name is required");
    if (onboardForm.username.length < 3) return show("Username must be at least 3 characters");
    if (onboardForm.password.length < 6) return show("Password must be at least 6 characters");
    try {
      const r = await axios.post(`${API}/admin/onboard-partner`, onboardForm, adminAuthHeader(admin.token));
      setOnboardForm({ username: "", password: "", name: "", business_type: "Grooming", area: "", pincode: "", phone: "", description: "", address: "", maps_url: "" });
      await loadAdmin(); await loadProviders("");
      show(`Onboarded ${r.data.provider.name} (${r.data.partner.username})`);
    } catch (e) { show(e.response?.data?.detail || "Could not onboard"); }
  };

  const savePartnerProfile = async () => {
    try {
      await axios.patch(`${API}/partner/profile`, profileForm, partnerAuthHeader(partner.token));
      await loadPartner(); await loadProviders("");
      show("Business profile saved");
    } catch (e) { show(e.response?.data?.detail || "Could not save"); }
  };

  const downloadPayoutCSV = async (month) => {
    try {
      const r = await axios.get(`${API}/partner/payout-export`, { ...partnerAuthHeader(partner.token), params: month ? { month } : {}, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `tailwhisk-payout-${month || "all"}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      URL.revokeObjectURL(url);
      show("CSV downloaded");
    } catch (e) { show("Could not download CSV"); }
  };

  const regenerateSlots = async () => {
    try {
      await axios.post(`${API}/partner/slots/regenerate`, slotSettings, partnerAuthHeader(partner.token));
      await loadPartner();
      show("Opening hours saved — customers see the new slots instantly");
    } catch (e) { show(e.response?.data?.detail || "Could not save hours"); }
  };
  const toggleClosedDay = (day) => {
    setSlotSettings((prev) => ({ ...prev, closed_days: prev.closed_days.includes(day) ? prev.closed_days.filter((d) => d !== day) : [...prev.closed_days, day] }));
  };

  const openEditProvider = (p) => { setEditingProvider(p); setEditForm({ name: p.name, business_type: (p.type || "").toLowerCase().includes("vet") ? "Clinic" : "Grooming", area: p.area, pincode: p.pincode, phone: p.phone || "", description: p.description || "", address: p.address || "", maps_url: p.maps_url || "" }); };
  const saveEditProvider = async () => {
    try {
      await axios.patch(`${API}/admin/providers/${editingProvider.id}`, editForm, adminAuthHeader(admin.token));
      setEditingProvider(null);
      await loadAdmin(); await loadProviders("");
      show("Business updated");
    } catch (e) { show(e.response?.data?.detail || "Could not update"); }
  };
  const deleteProvider = async (p) => {
    if (!window.confirm(`Delete ${p.name}? This also removes its slots, partner logins, and coupons.`)) return;
    try {
      await axios.delete(`${API}/admin/providers/${p.id}`, adminAuthHeader(admin.token));
      await loadAdmin(); await loadProviders("");
      show(`Removed ${p.name}`);
    } catch (e) { show(e.response?.data?.detail || "Could not delete"); }
  };

  const refreshZones = async () => { const r = await axios.get(`${API}/zones`); setZones(r.data); };
  const createZone = async () => {
    if (!newZoneName.trim()) return show("Zone name is required");
    try {
      await axios.post(`${API}/admin/zones`, { name: newZoneName, areas: newZoneAreas.split(",").map((s) => s.trim()).filter(Boolean), pincodes: newZonePincodes.split(",").map((s) => s.trim()).filter(Boolean) }, adminAuthHeader(admin.token));
      setNewZoneName(""); setNewZoneAreas(""); setNewZonePincodes("");
      await refreshZones();
      show("Zone created");
    } catch (e) { show(e.response?.data?.detail || "Could not create zone"); }
  };
  const saveZone = async () => {
    if (!editingZone) return;
    try {
      await axios.patch(`${API}/admin/zones/${editingZone.id}`, { name: editingZone.name, areas: editingZone.areas, pincodes: editingZone.pincodes }, adminAuthHeader(admin.token));
      setEditingZone(null);
      await refreshZones();
      show("Zone updated");
    } catch (e) { show(e.response?.data?.detail || "Could not save zone"); }
  };
  const removeZone = async (z) => {
    if (!window.confirm(`Delete zone "${z.name}"? Providers in these areas will fall back to keyword search.`)) return;
    try {
      await axios.delete(`${API}/admin/zones/${z.id}`, adminAuthHeader(admin.token));
      await refreshZones();
      show("Zone removed");
    } catch (e) { show(e.response?.data?.detail || "Could not delete"); }
  };

  const partnerSummary = useMemo(() => ({
    pending: partnerCoupons.filter((c) => c.status === "pending").length,
    confirmed: partnerCoupons.filter((c) => c.status === "confirmed").length,
    rejected: partnerCoupons.filter((c) => c.status === "rejected").length,
    total: partnerCoupons.length,
  }), [partnerCoupons]);

  const filteredCoupons = useMemo(() => {
    const today = toLocalISO(new Date());
    const weekAhead = new Date(); weekAhead.setDate(weekAhead.getDate() + 7);
    const weekAheadIso = toLocalISO(weekAhead);
    return partnerCoupons.filter((c) => {
      if (c.status !== queueStatusTab) return false;
      if (queueDateFilter === "today") return c.date === today;
      if (queueDateFilter === "week") return c.date >= today && c.date <= weekAheadIso;
      return true;
    });
  }, [partnerCoupons, queueStatusTab, queueDateFilter]);

  const breedList = species === "dog" ? DOG_BREEDS : species === "cat" ? CAT_BREEDS : [];

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand" data-testid="brand-home" onClick={() => setMode("marketplace")}>
          <span className="brand-mark"><PawPrint size={20} /></span>
          <span>Tail<span>Whisk</span></span>
        </div>
        <div className="location-pill" data-testid="location-display"><MapPin size={16} /> Bengaluru {query && <><span>·</span> {query}</>}</div>
        <nav>
          <button data-testid="nav-marketplace" className={mode === "marketplace" ? "active" : ""} onClick={() => setMode("marketplace")}>Find care</button>
          <button data-testid="nav-coupons" className={mode === "coupons" ? "active" : ""} onClick={() => setMode("coupons")}>My coupons</button>
          <button data-testid="nav-insurance" className={mode === "insurance" ? "active" : ""} onClick={() => setMode("insurance")}><ShieldCheck size={16} /> Insurance</button>
          <button data-testid="nav-partner" className={mode === "partner" ? "active" : ""} onClick={() => setMode("partner")}><Store size={16} /> Partner</button>
          {admin && <button data-testid="nav-admin" className={mode === "admin" ? "active" : ""} onClick={() => setMode("admin")}><LayoutDashboard size={16} /> Admin</button>}
        </nav>
        <button data-testid="open-auth-button" className="profile-button" onClick={() => (user ? setMode("coupons") : setAuth(true))}>{user ? user.name?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase() : "Sign in"}</button>
      </header>

      {notice && <div className="toast" data-testid="toast-notice">{notice}</div>}

      {mode === "marketplace" && (
        <main>
          <section className="hero">
            <div className="hero-copy">
              <div className="eyebrow"><span className="live-dot" /> LIVE IN BENGALURU</div>
              <h1>Local pet care,<br /><em>better with a coupon.</em></h1>
              <p>Browse trusted grooming studios and vets near you, grab a running offer, and we'll lock the slot into a one-time coupon.</p>
              <div className="search-box">
                <Search size={20} />
                <input data-testid="location-search-input" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runSearch()} placeholder="Search area or pincode" />
                <button data-testid="location-search-button" onClick={runSearch}>Search</button>
              </div>
              <div className="quick-pins">
                <span>Try nearby</span>
                {["560038", "560034", "560102", "560066"].map((x) => (
                  <button data-testid={`pincode-${x}`} key={x} onClick={() => quickPin(x)}>{x}</button>
                ))}
              </div>
              <div className="type-filter" data-testid="type-filter">
                <span>Showing</span>
                <button data-testid="type-all" className={!typeFilter ? "type-pill selected" : "type-pill"} onClick={() => applyTypeFilter("")}>All</button>
                <button data-testid="type-grooming" className={typeFilter === "Grooming" ? "type-pill selected" : "type-pill"} onClick={() => applyTypeFilter("Grooming")}>Grooming</button>
                <button data-testid="type-clinic" className={typeFilter === "ClinicComingSoon" ? "type-pill selected" : "type-pill"} onClick={() => applyTypeFilter("ClinicComingSoon")}>Clinics <span className="soon-dot">soon</span></button>
                <button data-testid="type-events" className={typeFilter === "EventsComingSoon" ? "type-pill selected" : "type-pill"} onClick={() => applyTypeFilter("EventsComingSoon")}>Events <span className="soon-dot">soon</span></button>
              </div>
            </div>
            <div className="hero-image">
              <img src="https://images.pexels.com/photos/6816838/pexels-photo-6816838.jpeg" alt="Veterinarian caring for a dog" />
              <div className="image-note"><ShieldCheck size={18} /><span><b>Verified local providers</b><small>Coupons confirmed by the partner</small></span></div>
            </div>
          </section>

          <section className="feed">
            <div className="section-heading">
              <div><p className="eyebrow">DISCOVER CARE</p><h2>Places pet parents love</h2></div>
              <span className="provider-count" data-testid="provider-count">{providers.length} nearby options</span>
            </div>
            {(typeFilter === "ClinicComingSoon" || typeFilter === "EventsComingSoon") ? (
              <div className="coming-soon-card" data-testid="coming-soon-card">
                <div className="coming-soon-mark"><Clock3 size={28} /></div>
                <h3>{typeFilter === "ClinicComingSoon" ? "Clinics are coming soon" : "Pet events are coming soon"}</h3>
                <p className="muted">{typeFilter === "ClinicComingSoon" ? "We're onboarding Bengaluru's trusted vets. Clinic coupons will land here first." : "Adoption drives, free-vaccination camps, and training meet-ups will show up here."}</p>
                <button className="primary" data-testid="back-to-grooming" onClick={() => applyTypeFilter("Grooming")}>Browse grooming instead <ArrowRight size={16} /></button>
              </div>
            ) : (
              <div className="provider-grid">
              {providers.map((p) => (
                <article className="provider-card" key={p.id} data-testid={`provider-card-${p.id}`}>
                  <div className="provider-photo">
                    <img src={resolveImage(p.image)} alt={p.name} />
                    {p.maps_url ? (
                      <a className="distance" href={p.maps_url} target="_blank" rel="noreferrer" data-testid={`maps-link-${p.id}`} onClick={(e) => e.stopPropagation()}><MapPin size={13} />Open in Maps</a>
                    ) : (
                      <span className="distance"><MapPin size={13} />{p.distance}</span>
                    )}
                  </div>
                  <div className="provider-info">
                    <div className="provider-top">
                      <div><p className="eyebrow">{p.type}</p><h3>{p.name}</h3></div>
                      <span className="rating" title={p.rating_source ? `${p.rating_source} reviews` : ""}>★ {p.rating}{p.rating_source && <small> · {p.rating_source}</small>}</span>
                    </div>
                    <p className="muted">{p.area} · {p.services.length} services · {p.offers.length} offers</p>
                    <div className="service-tags">{p.offers.length ? p.offers.map((o) => <span key={o.id}>{o.label}</span>) : <span>No offers right now</span>}</div>
                    <button className="card-action" data-testid={`view-provider-${p.id}`} onClick={() => openProvider(p)}>View provider <ArrowRight size={16} /></button>
                  </div>
                </article>
              ))}
            </div>
            )}
          </section>
        </main>
      )}

      {mode === "coupons" && (
        <main className="dashboard-page">
          <div className="page-title">
            <div><p className="eyebrow">YOUR TAILWHISK</p><h1>Your coupons</h1><p className="muted">A one-time code per offer. Partner confirms it before your visit.</p></div>
            <button className="primary" data-testid="browse-care-button" onClick={() => setMode("marketplace")}>Find a new offer <ArrowRight size={16} /></button>
          </div>
          {!user ? (
            <div className="empty-state"><Mail size={32} /><h2>Sign in to see your coupons</h2><p>Use your email to receive and track one-time offer codes.</p><button className="primary" data-testid="empty-signin-button" onClick={() => setAuth(true)}>Sign in with email</button></div>
          ) : (
            <>
              <div className="stat-row">
                <div><span>Pending</span><strong data-testid="user-pending-count">{coupons.filter((c) => c.status === "pending").length}</strong></div>
                <div><span>Confirmed</span><strong data-testid="user-confirmed-count">{coupons.filter((c) => c.status === "confirmed").length}</strong></div>
                <div><span>Signed in as</span><strong style={{ fontSize: 15 }}>{user.email}</strong></div>
              </div>
              <h2 className="subheading">Coupon history</h2>
              <div className="booking-list">
                {coupons.length ? coupons.map((c) => (
                  <div className="booking-row" data-testid={`coupon-row-${c.id}`} key={c.id}>
                    <div className={`booking-icon ${c.status === "pending" ? "amber" : c.status === "rejected" ? "red" : ""}`}>{c.status === "pending" ? <Clock3 size={20} /> : c.status === "rejected" ? <X size={20} /> : <CheckCircle2 size={20} />}</div>
                    <div>
                      <b>{c.offer_label}</b>
                      <p>{c.provider_name} · {c.service_name} · {c.date} · {c.time}</p>
                      {c.status === "rejected" && c.rejection_reason && <p style={{ color: "#b91c1c", fontSize: 11, marginTop: 4 }}>Rejected: {c.rejection_reason}</p>}
                    </div>
                    <div className="booking-code"><span data-testid={`coupon-status-${c.id}`}>{c.status}</span><strong>{c.code}</strong></div>
                  </div>
                )) : <div className="empty-inline">Your coupon codes will appear here once you grab an offer.</div>}
              </div>
            </>
          )}
        </main>
      )}

      {mode === "insurance" && (
        <InsurancePage show={show} />
      )}

      {mode === "partner" && (
        <main className="dashboard-page">
          {!partner ? (
            <div className="auth-modal partner-login" data-testid="partner-login-card">
              <span className="auth-icon"><KeyRound /></span>
              <p className="eyebrow">PARTNER CONSOLE</p>
              <h2>Sign in to your business</h2>
              <p className="muted">Each business has its own private coupon queue and offer manager.</p>
              <input data-testid="partner-username-input" value={partnerUser} onChange={(e) => setPartnerUser(e.target.value)} placeholder="Username" />
              <input data-testid="partner-password-input" value={partnerPw} onChange={(e) => setPartnerPw(e.target.value)} placeholder="Password" type="password" />
              <button className="primary wide" data-testid="partner-login-button" onClick={partnerLogin}>Sign in <ArrowRight size={16} /></button>
              <p className="muted" style={{ fontSize: 11, marginTop: 18 }}>No account? Ask the TailWhisk admin to add your business.<br/>Email <a href="mailto:customer_support@tailwhisk.in" style={{ color: "var(--amber)", fontWeight: 700, textDecoration: "none" }} data-testid="partner-contact-email">customer_support@tailwhisk.in</a> or call <a href="tel:+917904579216" style={{ color: "var(--amber)", fontWeight: 700, textDecoration: "none" }} data-testid="partner-contact-phone">+91 79045 79216</a>.</p>
            </div>
          ) : (
            <>
              <div className="page-title">
                <div>
                  <p className="eyebrow">PARTNER CONSOLE</p>
                  <h1>{partnerProvider?.name || "Your business"}</h1>
                  <p className="muted">Signed in as <b>{partner.username}</b></p>
                </div>
                <button className="secondary" data-testid="partner-logout-button" onClick={partnerLogout}><LogOut size={16} /> Sign out</button>
              </div>

              <div className="photo-manager">
                {resolveImage(partnerProvider?.image) && <img src={resolveImage(partnerProvider?.image)} alt={partnerProvider?.name} className="photo-preview" />}
                <div className="photo-copy">
                  <p className="eyebrow">PROVIDER PHOTO</p>
                  <h3>Make a strong first impression</h3>
                  <p className="muted">A clear, well-lit photo of your space gets 2× more coupon requests.</p>
                  <input ref={photoInputRef} type="file" accept="image/*" data-testid="photo-upload-input" style={{ display: "none" }} onChange={(e) => uploadPhoto(e.target.files?.[0])} />
                  <button className="primary" data-testid="photo-upload-button" disabled={uploadingPhoto} onClick={() => photoInputRef.current?.click()}><Upload size={16} /> {uploadingPhoto ? "Uploading…" : "Upload new photo"}</button>
                </div>
              </div>

              <h2 className="subheading">Business profile</h2>
              <div className="offer-manager">
                <div className="offer-form profile-form">
                  <input data-testid="profile-name-input" value={profileForm.name} onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })} placeholder="Business / shop name" />
                  <select data-testid="profile-type-select" value={profileForm.business_type} onChange={(e) => setProfileForm({ ...profileForm, business_type: e.target.value })}>
                    <option value="Grooming">Grooming studio</option>
                    <option value="Clinic">Veterinary clinic</option>
                  </select>
                  <select data-testid="profile-area-input" value={profileForm.area} onChange={(e) => setProfileForm({ ...profileForm, area: e.target.value })}>
                    <option value="">Choose area…</option>
                    {zones.map((z) => (
                      <optgroup key={z.id} label={z.name}>
                        {z.areas.map((a) => <option key={`${z.id}-${a}`} value={a}>{a}</option>)}
                      </optgroup>
                    ))}
                  </select>
                  <input data-testid="profile-pincode-input" value={profileForm.pincode} onChange={(e) => setProfileForm({ ...profileForm, pincode: e.target.value })} placeholder="Pincode" />
                  <input data-testid="profile-phone-input" value={profileForm.phone} onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })} placeholder="Contact phone" />
                  <input data-testid="profile-description-input" value={profileForm.description} onChange={(e) => setProfileForm({ ...profileForm, description: e.target.value })} placeholder="Short description for customers" />
                  <button className="primary" data-testid="profile-save-button" onClick={savePartnerProfile}>Save profile</button>
                </div>
              </div>

              <div className="stat-row">
                <div><span>Pending</span><strong data-testid="partner-pending-count">{partnerSummary.pending}</strong></div>
                <div><span>Confirmed</span><strong data-testid="partner-confirmed-count">{partnerSummary.confirmed}</strong></div>
                <div><span>Rejected</span><strong data-testid="partner-rejected-count">{partnerSummary.rejected}</strong></div>
                <div className="payout-card"><span>Earned from confirmed</span><strong data-testid="partner-payout">₹{(partnerFunnel?.payout_total || 0).toLocaleString("en-IN")}</strong></div>
              </div>

              <h2 className="subheading">Services you provide</h2>
              <div className="offer-manager">
                <div className="offer-form service-form">
                  <input data-testid="service-name-input" value={newServiceName} onChange={(e) => setNewServiceName(e.target.value)} placeholder="Service name (e.g., De-shedding treatment)" />
                  <input data-testid="service-duration-input" value={newServiceDuration} onChange={(e) => setNewServiceDuration(e.target.value)} placeholder="Duration (e.g., 45 min)" />
                  <input data-testid="service-price-input" value={newServicePrice} onChange={(e) => setNewServicePrice(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Price ₹" inputMode="numeric" />
                  <button className="primary" data-testid="create-service-button" onClick={() => createService()}><Plus size={16} /> Add service</button>
                </div>
                <div className="service-tag-row">
                  <small>Quick add from popular services</small>
                  <div className="service-tag-list">
                    {(partnerProvider?.type?.toLowerCase().includes("vet") ? CLINIC_SUGGESTIONS : GROOMING_SUGGESTIONS).map((s) => {
                      const already = (partnerProvider?.services || []).some((x) => x.name.toLowerCase() === s.name.toLowerCase());
                      return (
                        <button key={s.name} type="button" data-testid={`suggest-${s.name.replace(/\s+/g, "-").toLowerCase()}`} className="service-tag" disabled={already} onClick={() => createService(s)} style={{ opacity: already ? 0.4 : 1, cursor: already ? "not-allowed" : "pointer" }}>
                          <Plus size={12} /> {s.name} · ₹{s.price}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="offer-chip-list">
                  {(partnerProvider?.services || []).map((s) => (
                    <div className="offer-chip service-chip" key={s.id} data-testid={`service-chip-${s.id}`}>
                      <div><b>{s.name}</b><small>{s.duration} · ₹{s.price}</small></div>
                      <button className="icon-btn" data-testid={`delete-service-${s.id}`} onClick={() => deleteService(s.id)}><Trash2 size={14} /></button>
                    </div>
                  ))}
                  {!(partnerProvider?.services || []).length && <p className="muted">No services yet — add one above or pick from the quick list.</p>}
                </div>
              </div>


              <h2 className="subheading">Available slot timings</h2>
              <div className="offer-manager">
                <div className="offer-form slot-settings-form">
                  <label>Open from<input data-testid="slot-start-input" type="time" value={slotSettings.start_time} onChange={(e) => setSlotSettings({ ...slotSettings, start_time: e.target.value })} /></label>
                  <label>Close at<input data-testid="slot-end-input" type="time" value={slotSettings.end_time} onChange={(e) => setSlotSettings({ ...slotSettings, end_time: e.target.value })} /></label>
                  <label>Break between slots<input data-testid="slot-break-input" type="number" min="0" max="60" value={slotSettings.break_minutes} onChange={(e) => setSlotSettings({ ...slotSettings, break_minutes: parseInt(e.target.value, 10) || 0 })} /></label>
                  <button className="primary" data-testid="regenerate-slots-button" onClick={regenerateSlots}>Save hours</button>
                </div>
                <div className="service-tag-row">
                  <small>Days closed (we won't show slots for these)</small>
                  <div className="service-tag-list">
                    {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((d) => {
                      const on = slotSettings.closed_days.includes(d);
                      return <button key={d} type="button" data-testid={`closed-day-${d}`} className={on ? "service-tag selected" : "service-tag"} onClick={() => toggleClosedDay(d)}>{on && <X size={13} />} {d.charAt(0).toUpperCase() + d.slice(1)}</button>;
                    })}
                  </div>
                </div>
                <p className="muted" style={{ fontSize: 11, margin: 0 }}>Each service's own duration becomes the slot length. Example: a 35-min service with a 10-min break fits as 10:00 → 10:35, 10:45 → 11:20, and so on.</p>
              </div>

              <h2 className="subheading">Who's finding you</h2>
              <div className="partner-funnel" data-testid="partner-funnel">
                {(partnerFunnel?.funnel || []).map((row, i) => {
                  const top = partnerFunnel?.funnel?.[0]?.count || 0;
                  const pct = top ? Math.round((row.count / top) * 100) : 0;
                  return (
                    <div className="funnel-row" key={row.label} data-testid={`funnel-${row.label.replace(/\s+/g, "-").toLowerCase()}`}>
                      <span className="funnel-step">0{i + 1}</span>
                      <div className="funnel-body">
                        <div className="funnel-top"><b>{row.label}</b><strong>{row.count}</strong></div>
                        <div className="funnel-bar"><div style={{ width: `${pct}%` }} /></div>
                      </div>
                    </div>
                  );
                })}
                {partnerFunnel && (
                  <div className="funnel-footer">
                    <span>Confirmation rate <b>{partnerFunnel.conversion_rate}%</b></span>
                    <span>·</span>
                    <span>Shareable link <a href={`/p/${partnerProvider?.id}`} target="_blank" rel="noopener noreferrer" data-testid="partner-share-link">{window.location.origin}/p/{partnerProvider?.id}</a></span>
                    <div className="payout-exports">
                      <button className="secondary" data-testid="payout-export-month" onClick={() => downloadPayoutCSV(new Date().toISOString().slice(0,7))}>This month's CSV</button>
                      <button className="secondary" data-testid="payout-export-all" onClick={() => downloadPayoutCSV()}>All-time CSV</button>
                    </div>
                  </div>
                )}
              </div>


              <h2 className="subheading">Offers you run</h2>
              <div className="offer-manager">
                <div className="offer-form">
                  <input data-testid="offer-label-input" value={newOfferLabel} onChange={(e) => setNewOfferLabel(e.target.value)} placeholder="Offer label (e.g., 15% off nail trim)" />
                  <input data-testid="offer-detail-input" value={newOfferDetail} onChange={(e) => setNewOfferDetail(e.target.value)} placeholder="Details (e.g., Monday to Thursday only)" />
                  <button className="primary" data-testid="create-offer-button" onClick={createOffer}><Plus size={16} /> Publish offer</button>
                </div>
                <div className="service-tag-row">
                  <small>Tag services this offer applies to <em>(leave empty to apply to all)</em></small>
                  <div className="service-tag-list">
                    {(partnerProvider?.services || []).map((s) => {
                      const checked = newOfferServiceIds.includes(s.id);
                      return (
                        <button key={s.id} type="button" data-testid={`offer-service-tag-${s.id}`} className={checked ? "service-tag selected" : "service-tag"} onClick={() => setNewOfferServiceIds((prev) => prev.includes(s.id) ? prev.filter((x) => x !== s.id) : [...prev, s.id])}>
                          {checked && <CheckCircle2 size={13} />} {s.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="offer-chip-list">
                  {(partnerProvider?.offers || []).map((o) => {
                    const tagged = (o.service_ids || []).map((sid) => partnerProvider?.services?.find((s) => s.id === sid)?.name).filter(Boolean);
                    return (
                      <div className="offer-chip" key={o.id} data-testid={`offer-chip-${o.id}`}>
                        <div>
                          <b>{o.label}</b>
                          <small>{tagged.length ? `For: ${tagged.join(", ")}` : "All services"}</small>
                        </div>
                        <button className="icon-btn" data-testid={`delete-offer-${o.id}`} onClick={() => deleteOffer(o.id)}><Trash2 size={14} /></button>
                      </div>
                    );
                  })}
                  {!(partnerProvider?.offers || []).length && <p className="muted">No offers yet. Publish your first one above.</p>}
                </div>
              </div>

              <h2 className="subheading" style={{ marginTop: 36 }}>Coupon queue</h2>
              <div className="queue-controls">
                <div className="queue-tabs">
                  {[["pending","Pending",partnerSummary.pending],["confirmed","Confirmed",partnerSummary.confirmed],["rejected","Rejected",partnerSummary.rejected]].map(([key,label,count]) => (
                    <button key={key} data-testid={`queue-tab-${key}`} className={queueStatusTab===key?"queue-tab selected":"queue-tab"} onClick={() => setQueueStatusTab(key)}>
                      {label} <span className="queue-count">{count}</span>
                    </button>
                  ))}
                </div>
                <div className="queue-dates">
                  {[["all","All"],["today","Today"],["week","Next 7 days"]].map(([key,label]) => (
                    <button key={key} data-testid={`queue-date-${key}`} className={queueDateFilter===key?"type-pill selected":"type-pill"} onClick={() => setQueueDateFilter(key)}>{label}</button>
                  ))}
                </div>
              </div>

              {queueStatusTab === "rejected" && rejectionStats && rejectionStats.total_rejected > 0 && (
                <div className="rejection-analytics" data-testid="rejection-analytics">
                  <div className="rejection-head">
                    <TrendingDown size={18} /> <h3>Rejection analytics</h3>
                    <span className="rejection-rate">{rejectionStats.rejection_rate}% rejection rate</span>
                  </div>
                  <div className="rejection-reasons">
                    {rejectionStats.reasons.map((r) => (
                      <div className="rejection-reason-row" key={r.reason} data-testid={`rejection-reason-${r.count}`}>
                        <div className="rejection-reason-bar"><div style={{ width: `${(r.count / rejectionStats.total_rejected) * 100}%` }} /></div>
                        <span>{r.reason}</span>
                        <b>{r.count}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="booking-list">
                {filteredCoupons.length ? filteredCoupons.map((c) => (
                  <div className="partner-coupon-card" data-testid={`partner-coupon-row-${c.id}`} key={c.id}>
                    <div className="partner-coupon-head">
                      <div>
                        <span className={`status-chip ${c.status}`} data-testid={`partner-coupon-status-${c.id}`}>{c.status}</span>
                        <b className="coupon-code-text">{c.code}</b>
                      </div>
                      <span className="offer-mark"><Ticket size={16} /></span>
                    </div>
                    <p className="partner-coupon-offer"><b>{c.offer_label}</b> · {c.service_name}</p>
                    <div className="partner-coupon-grid">
                      <div><span>Customer</span><b data-testid={`partner-customer-${c.id}`}>{c.customer_name || "—"}</b></div>
                      <div><span>Phone</span>{c.phone ? <a href={`tel:${c.phone}`} data-testid={`partner-call-${c.id}`} className="call-link">{c.phone}</a> : <b>—</b>}</div>
                      <div><span>Pet</span><b data-testid={`partner-pet-${c.id}`}>{c.species ? `${c.species} · ` : ""}{c.pet_name || "—"}{c.breed ? ` (${c.breed})` : ""}</b></div>
                      <div><span>Slot</span><b>{c.date} · {c.time}</b></div>
                      <div><span>Email</span><b style={{ fontSize: 12 }}>{c.email}</b></div>
                      {c.pet_notes && <div><span>Note</span><b style={{ fontSize: 12 }}>{c.pet_notes}</b></div>}
                      {c.status === "rejected" && c.rejection_reason && <div style={{ gridColumn: "1 / -1" }}><span>Rejection reason</span><b style={{ fontSize: 12, color: "#b91c1c" }}>{c.rejection_reason}</b></div>}
                    </div>
                    <div className="partner-coupon-actions">
                      {c.phone && <a className="secondary" href={`tel:${c.phone}`} data-testid={`partner-call-btn-${c.id}`}>Call customer</a>}
                      {c.status === "pending" && (
                        <>
                          <button className="danger-outline" data-testid={`partner-reject-${c.id}`} onClick={() => openReject(c)}><X size={16} /> Reject</button>
                          <button className="primary" data-testid={`partner-confirm-${c.id}`} onClick={() => confirmPartnerCoupon(c.id)}>Confirm coupon <CheckCircle2 size={16} /></button>
                        </>
                      )}
                      {c.status === "confirmed" && <span className="confirmed-tag"><CheckCircle2 size={16} /> Confirmed</span>}
                      {c.status === "rejected" && <span className="rejected-tag"><X size={16} /> Rejected</span>}
                    </div>
                  </div>
                )) : <div className="empty-inline">No {queueStatusTab} coupons{queueDateFilter !== "all" ? ` ${queueDateFilter === "today" ? "today" : "in the next 7 days"}` : ""} yet.</div>}
              </div>
            </>
          )}
        </main>
      )}

      {mode === "admin" && (
        <main className="dashboard-page">
          {!admin ? (
            <div className="auth-modal partner-login" data-testid="admin-login-card">
              <span className="auth-icon"><KeyRound /></span>
              <p className="eyebrow">TAILWHISK ADMIN</p>
              <h2>Super-admin sign in</h2>
              <p className="muted">This console is private. Credentials are managed outside the app.</p>
              <input data-testid="admin-login-username" value={adminLoginUser} onChange={(e) => setAdminLoginUser(e.target.value)} placeholder="Username" />
              <input data-testid="admin-login-password" value={adminLoginPw} onChange={(e) => setAdminLoginPw(e.target.value)} placeholder="Password" type="password" onKeyDown={(e) => e.key === "Enter" && adminLoginSubmit()} />
              <button className="primary wide" data-testid="admin-login-submit" onClick={adminLoginSubmit}>Sign in <ArrowRight size={16} /></button>
            </div>
          ) : (
            <>
              <div className="page-title">
                <div><p className="eyebrow">TAILWHISK ADMIN</p><h1>Platform signals</h1><p className="muted">Signed in as <b>{admin.username}</b></p></div>
                <button className="secondary" data-testid="admin-logout-button" onClick={adminLogout}><LogOut size={16} /> Sign out</button>
              </div>
          <div className="stat-row">
            <div><span>Providers live</span><strong data-testid="admin-providers">{adminSummary?.providers ?? "—"}</strong></div>
            <div><span>Partners</span><strong data-testid="admin-partner-count">{adminSummary?.partners ?? "—"}</strong></div>
            <div><span>Pending coupons</span><strong data-testid="admin-pending">{adminSummary?.pending_coupons ?? "—"}</strong></div>
            <div><span>Confirmed coupons</span><strong data-testid="admin-confirmed">{adminSummary?.confirmed_coupons ?? "—"}</strong></div>
          </div>

          <h2 className="subheading">Onboard a new partner</h2>
          <div className="offer-manager">
            <div className="offer-form onboard-form">
              <input data-testid="onboard-business-name" value={onboardForm.name} onChange={(e) => setOnboardForm({ ...onboardForm, name: e.target.value })} placeholder="Business name (e.g., Snout & Paw Jayanagar)" />
              <select data-testid="onboard-business-type" value={onboardForm.business_type} onChange={(e) => setOnboardForm({ ...onboardForm, business_type: e.target.value })}>
                <option value="Grooming">Grooming studio</option>
                <option value="Clinic">Veterinary clinic</option>
              </select>
              <select data-testid="onboard-area" value={onboardForm.area} onChange={(e) => setOnboardForm({ ...onboardForm, area: e.target.value })}>
                <option value="">Choose area…</option>
                {zones.map((z) => (
                  <optgroup key={z.id} label={z.name}>
                    {z.areas.map((a) => <option key={`${z.id}-${a}`} value={a}>{a}</option>)}
                  </optgroup>
                ))}
              </select>
              <input data-testid="onboard-pincode" value={onboardForm.pincode} onChange={(e) => setOnboardForm({ ...onboardForm, pincode: e.target.value })} placeholder="Pincode" />
              <input data-testid="onboard-phone" value={onboardForm.phone} onChange={(e) => setOnboardForm({ ...onboardForm, phone: e.target.value })} placeholder="Contact phone" />
              <input data-testid="onboard-description" value={onboardForm.description} onChange={(e) => setOnboardForm({ ...onboardForm, description: e.target.value })} placeholder="Short description" />
              <input data-testid="onboard-username" value={onboardForm.username} onChange={(e) => setOnboardForm({ ...onboardForm, username: e.target.value })} placeholder="Partner username" />
              <input data-testid="onboard-password" value={onboardForm.password} onChange={(e) => setOnboardForm({ ...onboardForm, password: e.target.value })} placeholder="Partner password (min 6)" type="text" />
              <button className="primary onboard-submit" data-testid="onboard-submit" onClick={onboardNewPartner}><Plus size={16} /> Onboard partner</button>
            </div>
            <p className="muted" style={{ fontSize: 11, margin: 0 }}>This creates the business, assigns a type, seeds 14 days of availability, and issues the partner's login in one step.</p>
          </div>

          <h2 className="subheading" style={{ marginTop: 32 }}>Link login to an existing business</h2>
          <div className="offer-manager">
            <div className="offer-form">
              <select data-testid="admin-provider-select" value={adminForm.provider_id} onChange={(e) => setAdminForm({ ...adminForm, provider_id: e.target.value })}>
                <option value="">Choose business…</option>
                {providers.map((p) => <option value={p.id} key={p.id}>{p.name}</option>)}
              </select>
              <input data-testid="admin-username-input" value={adminForm.username} onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })} placeholder="Username" />
              <input data-testid="admin-password-input" value={adminForm.password} onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })} placeholder="Password (min 6)" type="text" />
              <button className="primary" data-testid="admin-create-partner-button" onClick={createPartnerAccount}><Plus size={16} /> Create partner</button>
            </div>
            <div className="offer-chip-list">
              {adminPartners.length ? adminPartners.map((a) => (
                <div className="offer-chip" key={a.username} data-testid={`admin-partner-chip-${a.username}`}>
                  <div style={{ flex: 1 }}><b>{a.username}</b><small>{a.provider_name}</small></div>
                  <button className="icon-btn" data-testid={`edit-partner-creds-${a.username}`} onClick={() => openEditCreds(a)} title="Edit username / reset password" style={{ color: "#374151" }}>✎</button>
                </div>
              )) : <p className="muted">No partner accounts yet.</p>}
            </div>
          </div>


          <h2 className="subheading" style={{ marginTop: 32 }}>Bengaluru zones</h2>
          <p className="muted" style={{ fontSize: 12, marginTop: -8 }}>Each zone groups the areas and pincodes we treat as "nearby" when customers search. Partners pick an area from this list during onboarding.</p>
          <div className="offer-manager">
            <div className="offer-form onboard-form">
              <input data-testid="zone-name-input" value={newZoneName} onChange={(e) => setNewZoneName(e.target.value)} placeholder="Zone name (e.g., Peripheral)" />
              <input data-testid="zone-areas-input" value={newZoneAreas} onChange={(e) => setNewZoneAreas(e.target.value)} placeholder="Areas (comma separated)" />
              <input data-testid="zone-pincodes-input" value={newZonePincodes} onChange={(e) => setNewZonePincodes(e.target.value)} placeholder="Pincodes (comma separated)" />
              <button className="primary" data-testid="create-zone-button" onClick={createZone}><Plus size={16} /> Add zone</button>
            </div>
            <div className="offer-chip-list zones-list" style={{ marginTop: 10 }}>
              {zones.map((z) => (
                <div className="offer-chip zone-chip" key={z.id} data-testid={`zone-chip-${z.id}`} style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <b style={{ flex: 1 }}>{z.name}</b>
                    <button className="icon-btn" data-testid={`edit-zone-${z.id}`} onClick={() => setEditingZone({ ...z })} style={{ color: "#374151" }}>✎</button>
                    <button className="icon-btn" data-testid={`delete-zone-${z.id}`} onClick={() => removeZone(z)}><Trash2 size={14} /></button>
                  </div>
                  <small>{z.areas.length} areas · {z.pincodes.length} pincodes</small>
                  <small style={{ color: "#78350f" }}>{z.areas.slice(0,4).join(", ")}{z.areas.length > 4 ? "…" : ""}</small>
                </div>
              ))}
            </div>
          </div>

          <h2 className="subheading" style={{ marginTop: 32 }}>All businesses</h2>
          <div className="booking-list">
            {adminProviders.length ? adminProviders.map((p) => (
              <div className="booking-row" key={p.id} data-testid={`admin-provider-row-${p.id}`}>
                <div className="booking-icon"><Store size={20} /></div>
                <div>
                  <b>{p.name}</b>
                  <p>{p.type} · {p.area} · {p.pincode}{p.phone ? ` · ${p.phone}` : ""}</p>
                </div>
                <div className="partner-coupon-actions" style={{ marginLeft: "auto" }}>
                  <a className="secondary" href={`/p/${p.id}`} target="_blank" rel="noopener noreferrer" data-testid={`admin-visit-${p.id}`}>Visit</a>
                  <button className="secondary" data-testid={`admin-edit-${p.id}`} onClick={() => openEditProvider(p)}>Edit</button>
                  <button className="danger-outline" data-testid={`admin-delete-${p.id}`} onClick={() => deleteProvider(p)}><Trash2 size={14} /> Delete</button>
                </div>
              </div>
            )) : <div className="empty-inline">No businesses yet.</div>}
          </div>

          <h2 className="subheading" style={{ marginTop: 36 }}>Onboarding funnel</h2>
          <div className="booking-list" data-testid="admin-funnel">
            {adminFunnel.map((f) => (
              <div className="booking-row" key={f.event}>
                <div className="booking-icon"><Activity size={20} /></div>
                <div><b>{f.event.replace(/_/g, " ")}</b><p className="muted" style={{ margin: 0 }}>Signal count</p></div>
                <div className="booking-code"><span>events</span><strong>{f.count}</strong></div>
              </div>
            ))}
          </div>

          <h2 className="subheading" style={{ marginTop: 32 }}>Recent events</h2>
          <div className="booking-list">
            {adminEvents.slice(0, 15).map((e, i) => (
              <div className="booking-row" key={i} data-testid={`admin-event-${i}`}>
                <div className="booking-icon"><ChevronRight size={20} /></div>
                <div><b>{e.event.replace(/_/g, " ")}</b><p>{e.email || "anonymous"} · {new Date(e.created_at).toLocaleString()}</p></div>
              </div>
            ))}
          </div>
            </>
          )}
        </main>
      )}

      {selected && (
        <div className="modal-backdrop" data-testid="provider-modal">
          <div className="provider-modal">
            <button className="close" data-testid="close-provider-modal" onClick={() => setSelected(null)}><X /></button>
            <div className="modal-cover">
              <img src={resolveImage(selected.image)} alt={selected.name} />
              <div>
                <p className="eyebrow">{selected.type} · {selected.area}</p>
                <h2>{selected.name}</h2>
                <p>{selected.description}</p>
              </div>
            </div>
            <div className="tabs">
              {["overview", "services", "offers", "coupon"].map((t) => (
                <button data-testid={`provider-tab-${t}`} className={tab === t ? "selected" : ""} onClick={() => setTab(t)} key={t}>{t}</button>
              ))}
            </div>
            {tab === "overview" && (
              <div className="modal-body">
                <div className="detail-line"><ShieldCheck size={18} /><span><b>Verified provider</b><small>Local team{selected.phone ? ` · ${selected.phone}` : ""}</small></span></div>
                <div className="detail-line"><MapPin size={18} /><span><b>{selected.area}, Bengaluru</b><small>{selected.address || "Address not published yet"}</small></span></div>
                {selected.maps_url && (
                  <a className="secondary wide" href={selected.maps_url} target="_blank" rel="noreferrer" data-testid="open-in-maps-button" style={{ marginBottom: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}><MapPin size={16} /> Open in Google Maps</a>
                )}
                <button className="primary wide" data-testid="start-coupon-button" onClick={() => setTab("coupon")}>Grab a coupon <ArrowRight size={16} /></button>
              </div>
            )}
            {tab === "services" && (
              <div className="modal-body service-list" data-testid="services-list" style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {selected.services.map((s) => (
                  <div className="service-row" key={s.id}><div><b>{s.name}</b><small>{s.duration}</small></div><strong>{s.price > 0 ? `₹${s.price}` : "On request"}</strong></div>
                ))}
              </div>
            )}
            {tab === "offers" && (
              <div className="modal-body service-list">
                {selected.offers.length ? selected.offers.map((o) => {
                  const tagged = (o.service_ids || []).map((sid) => selected.services.find((s) => s.id === sid)?.name).filter(Boolean);
                  return (
                    <div className="offer-row" key={o.id}>
                      <span className="offer-mark">%</span>
                      <div>
                        <b>{o.label}</b>
                        <small>{o.detail}{tagged.length ? ` · For: ${tagged.join(", ")}` : ""}</small>
                      </div>
                    </div>
                  );
                }) : <p className="muted">No active offers for this provider.</p>}
              </div>
            )}
            {tab === "coupon" && (
              <div className="modal-body booking-panel">
                {!selected.offers.length ? (
                  <p className="muted">This provider has no running offers right now.</p>
                ) : (
                  <>
                    <label>Service<select data-testid="service-select" value={chosenService?.id || ""} onChange={(e) => { setChosenService(selected.services.find((s) => s.id === e.target.value)); setChosenOffer(null); }}>
                      {selected.services.map((s) => <option value={s.id} key={s.id}>{s.name}{s.price > 0 ? ` · ₹${s.price}` : ""}</option>)}
                    </select></label>

                    <div className="date-time-grid">
                      <div>
                        <label style={{ display: "block", marginBottom: 8 }}>Pick a date</label>
                        <div className="calendar-wrapper" data-testid="date-calendar">
                          <Calendar
                            mode="single"
                            selected={chosenDate}
                            onSelect={(d) => { setChosenDate(d); setChosenSlot(null); }}
                            disabled={(day) => !slotDates.includes(toLocalISO(day))}
                            defaultMonth={slotDates[0] ? new Date(slotDates[0] + "T00:00:00") : new Date()}
                          />
                        </div>
                      </div>
                      <div>
                        <label style={{ display: "block", marginBottom: 8 }}>Available times</label>
                        {chosenDate ? (
                          <div className="slot-column">
                            {timesForDate.length ? timesForDate.map((s) => (
                              <button data-testid={`slot-${s.id}`} className={chosenSlot?.id === s.id ? "slot-pill selected" : s.available === false ? "slot-pill taken" : "slot-pill"} disabled={s.available === false} onClick={() => pickSlot(s)} key={s.id}>
                                <b>{s.time}</b>
                                <small>{s.available === false ? "booked" : `→ ${s.end_time}`}</small>
                              </button>
                            )) : <p className="muted" style={{ fontSize: 12 }}>No times on this day.</p>}
                          </div>
                        ) : <p className="muted" style={{ fontSize: 12 }}>Pick a date to see times.</p>}
                      </div>
                    </div>

                    {selected.offers.length > 0 && (
                    <label>Offer to redeem
                      <select data-testid="offer-select" value={chosenOffer?.id || ""} onChange={(e) => pickOffer(e.target.value)}>
                        <option value="">Choose an offer…</option>
                        {selected.offers
                          .filter((o) => !(o.service_ids || []).length || !chosenService || (o.service_ids || []).includes(chosenService.id))
                          .map((o) => <option key={o.id} value={o.id}>{o.label}{(o.service_ids || []).length ? " · tagged" : ""}</option>)}
                      </select>
                      {chosenService && !selected.offers.some((o) => !(o.service_ids || []).length || (o.service_ids || []).includes(chosenService.id)) && (
                        <small style={{ color: "#b45309", display: "block", marginTop: 6 }}>No offers available for this service — pick a different service above.</small>
                      )}
                    </label>
                    )}

                    <div>
                      <label style={{ display: "block", marginBottom: 8 }}>Your pet is a…</label>
                      <div className="species-toggle">
                        <button type="button" data-testid="species-dog-btn" className={species === "dog" ? "species-btn selected" : "species-btn"} onClick={() => { setSpecies("dog"); setBreed(""); }}><Dog size={18} /> Dog</button>
                        <button type="button" data-testid="species-cat-btn" className={species === "cat" ? "species-btn selected" : "species-btn"} onClick={() => { setSpecies("cat"); setBreed(""); }}><Cat size={18} /> Cat</button>
                      </div>
                    </div>

                    {species && (
                      <label>Breed
                        <select data-testid="breed-select" value={breed} onChange={(e) => setBreed(e.target.value)}>
                          <option value="">Choose breed…</option>
                          {breedList.map((b) => <option key={b} value={b}>{b}</option>)}
                          <option value="Other">Other</option>
                        </select>
                      </label>
                    )}
                    {breed === "Other" && (
                      <label>Breed name<input data-testid="breed-other-input" value={breedOther} onChange={(e) => setBreedOther(e.target.value)} placeholder="e.g., Lhasa Apso" /></label>
                    )}

                    <div className="contact-grid">
                      <label>Your name<input data-testid="customer-name-input" value={custName} onChange={(e) => setCustName(e.target.value)} placeholder="e.g., Riya Menon" /></label>
                      <label>Phone<input data-testid="customer-phone-input" value={custPhone} onChange={(e) => setCustPhone(e.target.value)} placeholder="10-digit mobile" inputMode="tel" /></label>
                      <label>Pet's name<input data-testid="pet-name-input" value={petName} onChange={(e) => setPetName(e.target.value)} placeholder="e.g., Milo" /></label>
                      <label>Pet note (optional)<input data-testid="pet-notes-input" value={petNotes} onChange={(e) => setPetNotes(e.target.value)} placeholder="Age, allergies, temperament…" /></label>
                    </div>

                    <div className="booking-summary">
                      <span>Coupon stays <b>pending</b> until the partner confirms</span>
                      <button className="primary" data-testid="request-coupon-button" onClick={requestCoupon}>Request coupon <ArrowRight size={16} /></button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {editingZone && (
        <div className="modal-backdrop" data-testid="zone-edit-modal">
          <div className="auth-modal" style={{ textAlign: "left", maxWidth: 480 }}>
            <button className="close" data-testid="close-zone-edit" onClick={() => setEditingZone(null)}><X /></button>
            <p className="eyebrow">EDIT ZONE</p>
            <h2 style={{ marginBottom: 14 }}>{editingZone.name}</h2>
            <input data-testid="edit-zone-name" value={editingZone.name} onChange={(e) => setEditingZone({ ...editingZone, name: e.target.value })} placeholder="Zone name" />
            <textarea data-testid="edit-zone-areas" value={editingZone.areas.join(", ")} onChange={(e) => setEditingZone({ ...editingZone, areas: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Areas (comma separated)" rows={3} style={{ marginTop: 10 }} />
            <textarea data-testid="edit-zone-pincodes" value={editingZone.pincodes.join(", ")} onChange={(e) => setEditingZone({ ...editingZone, pincodes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Pincodes (comma separated)" rows={2} />
            <button className="primary wide" data-testid="save-zone-edit" onClick={saveZone}>Save zone <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {editingProvider && (
        <div className="modal-backdrop" data-testid="admin-edit-modal">
          <div className="auth-modal" style={{ textAlign: "left", maxWidth: 480 }}>
            <button className="close" data-testid="close-admin-edit" onClick={() => setEditingProvider(null)}><X /></button>
            <p className="eyebrow">EDIT BUSINESS</p>
            <h2 style={{ marginBottom: 14 }}>{editingProvider.name}</h2>
            <input data-testid="edit-name-input" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder="Business name" />
            <select data-testid="edit-type-select" value={editForm.business_type} onChange={(e) => setEditForm({ ...editForm, business_type: e.target.value })} style={{ width: "100%", height: 42, border: "1px solid #d1d5db", borderRadius: 7, padding: "0 12px", marginTop: 10, background: "#fff" }}>
              <option value="Grooming">Grooming studio</option>
              <option value="Clinic">Veterinary clinic</option>
            </select>
            <input data-testid="edit-area-input" value={editForm.area || ""} onChange={(e) => setEditForm({ ...editForm, area: e.target.value })} placeholder="Area" />
            <input data-testid="edit-pincode-input" value={editForm.pincode || ""} onChange={(e) => setEditForm({ ...editForm, pincode: e.target.value })} placeholder="Pincode" />
            <input data-testid="edit-phone-input" value={editForm.phone || ""} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} placeholder="Phone" />
            <input data-testid="edit-description-input" value={editForm.description || ""} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} placeholder="Description" />
            <input data-testid="edit-address-input" value={editForm.address || ""} onChange={(e) => setEditForm({ ...editForm, address: e.target.value })} placeholder="Full address" />
            <input data-testid="edit-maps-url-input" value={editForm.maps_url || ""} onChange={(e) => setEditForm({ ...editForm, maps_url: e.target.value })} placeholder="Google Maps link (optional)" />
            <button className="primary wide" data-testid="save-admin-edit" onClick={saveEditProvider}>Save changes <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {editCreds && (
        <div className="modal-backdrop" data-testid="edit-creds-modal">
          <div className="auth-modal" style={{ textAlign: "left", maxWidth: 440 }}>
            <button className="close" data-testid="close-edit-creds" onClick={closeEditCreds}><X /></button>
            <p className="eyebrow">EDIT PARTNER CREDENTIALS</p>
            <h2 style={{ marginBottom: 4 }}>{editCreds.provider_name}</h2>
            <p className="muted" style={{ fontSize: 12, marginBottom: 14 }}>Current username: <b>{editCreds.current_username}</b>. Changing either will sign them out of any active session.</p>
            <input data-testid="edit-creds-username" value={editCreds.new_username} onChange={(e) => setEditCreds({ ...editCreds, new_username: e.target.value })} placeholder="New username (min 3 chars)" />
            <input data-testid="edit-creds-password" value={editCreds.new_password} onChange={(e) => setEditCreds({ ...editCreds, new_password: e.target.value })} placeholder="New password (min 6 chars, leave blank to keep)" type="text" style={{ marginTop: 10 }} />
            <button className="primary wide" data-testid="save-edit-creds" onClick={saveEditCreds}>Update credentials <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {rejectTarget && (
        <div className="modal-backdrop" data-testid="reject-modal">
          <div className="auth-modal">
            <button className="close" data-testid="close-reject-modal" onClick={closeReject}><X /></button>
            <span className="auth-icon" style={{ background: "#fee2e2", color: "#b91c1c" }}><X /></span>
            <p className="eyebrow">REJECT COUPON</p>
            <h2>Why can't you honour this?</h2>
            <p className="muted">The customer will get an SMS with the reason so they can rebook.</p>
            <div className="reason-chips">
              {["Slot got booked earlier", "Fully booked for the day", "Service unavailable right now", "Pet type not accepted"].map((r) => (
                <button key={r} type="button" data-testid={`reject-preset-${r.replace(/\s+/g, "-").toLowerCase()}`} className="type-pill" onClick={() => setRejectReason(r)}>{r}</button>
              ))}
            </div>
            <textarea data-testid="reject-reason-input" value={rejectReason} onChange={(e) => setRejectReason(e.target.value.slice(0, 240))} placeholder="Add a message for the customer (max 240 chars)" rows={3} />
            <small style={{ color: "#6b7280", display: "block", textAlign: "right", margin: "4px 0 12px" }}>{rejectReason.length}/240</small>
            <button className="danger wide" data-testid="submit-reject-button" onClick={submitReject}>Send rejection <ArrowRight size={16} /></button>
          </div>
        </div>
      )}

      {auth && (
        <div className="modal-backdrop" data-testid="auth-modal">
          <div className="auth-modal">
            <button className="close" data-testid="close-auth-modal" onClick={() => { setAuth(false); setPendingRequest(null); }}><X /></button>
            <span className="auth-icon"><Mail /></span>
            <p className="eyebrow">WELCOME TO TAILWHISK</p>
            <h2>Verify to claim your coupon.</h2>
            <p className="muted">We send a one-time code to your email so your coupon stays tied to you.</p>
            {!sent ? (
              <>
                <input data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={demoEmail} type="email" />
                <button className="primary wide" data-testid="send-otp-button" onClick={sendOtp}>Send me a code <ArrowRight size={16} /></button>
              </>
            ) : (
              <>
                <p className="demo-code" data-testid="otp-helper-text">Check your inbox (and spam) for the 6-digit code.</p>
                <input data-testid="otp-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter six-digit code" inputMode="numeric" />
                <button className="primary wide" data-testid="verify-otp-button" onClick={verify}>Verify email <ArrowRight size={16} /></button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
